import CoachingDomainPage from './CoachingDomainPage';
import bandpic from "../resources/EDUVIC B2C Hero3-coaching.png";

const longDescription = `
# 📘 Selective Entry Test Victoria — Year 9

## 🧠 Understand the Examination

The **Victoria Selective Entry High Schools program** provides entry into Year 9 at four academically selective government schools:

| Selective Entry High School | Location |
|---|---|
| **Melbourne High School** | South Yarra |
| **The Mac.Robertson Girls' High School** | Melbourne |
| **Nossal High School** | Berwick |
| **Suzanne Cory High School** | Werribee |

The examination is highly competitive and assesses more than syllabus knowledge. Students need to apply concepts to unfamiliar problems while maintaining **accuracy, reasoning ability and speed under examination conditions**.

### 📝 Examination Structure

| Examination Component | Duration |
|---|---:|
| ➗ **Mathematics & Quantitative Reasoning** | **60 minutes** |
| 📖 **Reading & Verbal Reasoning** | **55 minutes** |
| ✍️ **Writing — 2 Extended Writing Tasks** | **40 minutes** |

### 🎯 What the Examination Tests

- 🧠 **Logical reasoning** — identify patterns, relationships and rules
- ➗ **Mathematical reasoning** — apply mathematical concepts to unfamiliar problems
- 📖 **Reading and comprehension** — understand and interpret information accurately and efficiently
- ✍️ **Writing ability** — communicate ideas clearly and effectively
- 💡 **Problem solving** — work through unfamiliar and challenging questions
- ⏱️ **Accuracy and speed** — maintain performance under strict time pressure

Successful preparation therefore requires more than simply covering content. Students need to become comfortable with unfamiliar question types, develop reliable problem-solving approaches, manage time effectively and learn from their performance.

---

## 👨‍🏫 Guided Preparation with Eduvic

**Eduvic Coaching provides guided preparation through scheduled online classes, structured independent practice and regular assessment.** The Eduvic Learning Platform brings these elements together, while relevant Eduvic Practice Platform resources are provided as **complimentary access** for students undertaking Guided Preparation.

### 🔄 How Guided Preparation Works

| Stage | How it supports preparation |
|---|---|
| **1. Build the Foundation** | Scheduled classes establish the underlying concepts and address foundation gaps before progressing to examination-level problems. |
| **2. Clarify, Apply & Improve** | Students receive doubt-clearing, problem-solving guidance and practical techniques for improving speed, accuracy and examination technique. |
| **3. Assess Through the Learning Platform** | Scheduled assessments and examinations provide regular opportunities to test preparation under controlled conditions. |
| **4. Practice, Review & Consolidate** | Complimentary access to relevant Eduvic Practice resources allows students to continue practising independently between guided sessions and use performance information to identify areas requiring further attention. |

### 📚 The Eduvic Learning Environment

Guided Preparation is delivered through the **Eduvic Learning Platform**, which provides the online environment for the different stages of a student's preparation.

| **Eduvic Learning Platform** |
| --- |
| 🧑‍🏫 **Scheduled Online Classes** — structured teaching and guided problem-solving focused on the requirements of the examination |
| 📚 **Digital Lessons & Learning Material** — class content and supporting material available for revision between sessions |
| 📝 **Scheduled Assessments** — regular assessments used to check understanding and preparation |
| 🧪 **Guided Preparation Examinations** — examination-style assessments conducted as part of the preparation program |
| 📈 **Performance & Progress** — results and assessment information used to identify areas requiring further attention |

Where appropriate, Guided Preparation also incorporates **complimentary access to relevant Eduvic Practice Platform resources**. This provides an additional practice layer between guided sessions, allowing students to apply concepts, work through unfamiliar questions and develop speed and accuracy through repeated test-based practice.

| **Eduvic Practice Platform** |
| --- |
| 🔄 **Practice Between Guided Sessions** — students can continue applying concepts and practising examination-style questions between scheduled classes |
| ⏱️ **Timed & Untimed Practice** — practise for understanding when required, or introduce time pressure as examination readiness develops |
| 📊 **Practice Performance** — use practice results to identify areas that may require further explanation, revision or additional work |
| 🔎 **Review & Consolidation** — revisit incorrect questions, explanations and weaker areas to reinforce learning |

The two environments therefore form part of the **same Guided Preparation pathway**: teaching and learning are delivered through the Eduvic Learning Platform, while relevant Practice Platform resources provide additional opportunities to **apply, practise and consolidate** what is covered during guided preparation.
`;

const VictoriaSelectiveEntryYear9Page = () => (
  <CoachingDomainPage
    bandImage={bandpic}
    title="Victoria Selective Entry"
    subtitle="Target Year 9"
    accent="teal"
    longDescription={longDescription}
  />
);

export default VictoriaSelectiveEntryYear9Page;